import logger from '../../utils/logger-rum';
import * as Logger from '../../utils/logger';
/* eslint-disable dot-notation */
/* eslint-disable no-param-reassign */
/* eslint-disable no-async-promise-executor */
import React, {
	Context,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	useCallback,
} from 'react';
import {
	batch,
	useDispatch,
	useSelector,
} from 'react-redux';
import {
	Publisher,
	Stream,
	Subscriber,
} from '@opentok/client';
import {
	setRestrictedUserState,
	setScreenShareWebrtcId,
	setWebrtcId,
	userUpdated,
} from '../../stores/actions/session';
import {
	addPresenterVideo,
	removePresenterVideo,
	setConnectedPresenterVideos,
	setSharingScreen,
	updateLocalId,
	addFeedWebRTCId,
	removeFeedWebRTCId,
	clearFeedWebRTCIds,
	setMainVideoStream,
	setShouldRepublishVideo,
	setSelectedVideo,
	setSelectedAudio,
	setOnsiteLocationStreams,
	addOnsiteLocationStream,
	removeOnsiteLocationStream,
	resetOnsiteLocationStreams,
} from '../../stores/actions/video';
import {
	getUserIdFromMobileStream,
	isLocalUser,
	streamIsMobile,
	streamIsScreenshare,
	getProfileFromStreamName,
	isStreamScreenshare,
	parseStreamConnectionData,
} from '../../utils/utils';
import { GetTokboxToken, SetStreamInfo } from '../../connection/opentok';
import { OPENTOK_STREAM_AUDIO_REMOVE_UNMUTED_ID, setOpenTokStats } from '../../stores/actions/opentok';
import { GreenroomState as GRS } from '../../stores/types/types';
import OpenTok, { TotalOTStats } from '../../utils/video/opentok-connection';
import { AVContext } from '../audio-video';
import { VideoStream } from '../../components/types/videos';
import Socket from '../../connection/socket';
import { RESTRICTED_CONNECTION_TYPES, RESTRICTED_USER_STATES } from '../../stores/reducers/session';
import { ReportError } from '../../connection/errors';
import type { Shot, ShotFeed } from '../../stores/types/shots';
import { showAlert } from '../../components/general-components/alert-service';
import { ROOM_STATES } from '../../stores/reducers/settings';

import usePrevious from '../../utils/use-previous';
import store from '../../stores/main';
import { CONNECTED_PRESENTER_VIDEO_TYPES } from '../../types/constants';
import { getCameraStream } from '../../utils/video/video-streams';
import { selectOpentokSession } from '../../stores/selectors/session';
import { ProgramViewType } from '../../stores/types/session';
import type { OnsiteLocation, OnsiteLocationStream } from '../../stores/types/video';
import { OnsiteLocationSessionRoute } from '../../stores/types/video';
import type { ConnectedUser } from '../../stores/types/connection';
import { LOCATION_CANVAS_ROUTES } from '../../stores/reducers/video';

export interface OpenTokContextInterface {
	opentok: OpenTok | null;
	forceFeedsTabRefresh: boolean;
	setOT: Function;
	initialSetup: Function;
	restartPublisher: Function;
	changeAudioDevice: (device: MediaDeviceInfo) => void;
	changeVideoDevice: (device: MediaDeviceInfo) => void;
	stopOT: () => void;
	startupOpentokScreenshare: () => Promise<void>;
}

interface BackstageCommsSocketMessage {
	type: string;
	message: {
		muted: boolean;
		producerID: string;
		targetedPresenterID: string;
	};
}

interface ArchiveStreamInfo {
	streamId: string;
	grUser: number | undefined;
	deviceType: string;
	name: string | undefined;
}

// Everything archive-stream registration needs to resolve a stream's identity. 
interface ArchiveStreamSource {
	isoArchiveId: string | undefined;
	embed: boolean;
	token: string | null;
	meetingId: number | undefined;
	connectedUsers: ConnectedUser[];
	onsiteLocations: OnsiteLocation[];
}

export const OpenTokContext = React.createContext(
	{
		opentok: null,
		forceFeedsTabRefresh: false,
		setOT: () => {},
		initialSetup: () => {},
		restartPublisher: () => {},
	},
) as unknown as Context<OpenTokContextInterface>;

// these are the two types of event our listener fires for
export enum OTEventType {
	streamCreated = 'streamCreated',
	videoElementCreated = 'videoElementCreated',
}

// what you pass into the React.provider component as the value
export function useOpentok(socket: Socket | null): OpenTokContextInterface {
	const dispatch = useDispatch();

	// Skip OpenTok initialization for enabler-shot embeds
	const enablerShotParam = new URLSearchParams(window.location.search).get('enabler-shot');
	if (enablerShotParam) {
		return {
			opentok: null,
			forceFeedsTabRefresh: false,
			setOT: () => {},
			initialSetup: () => {},
			restartPublisher: () => {},
			changeAudioDevice: () => {},
			changeVideoDevice: () => {},
			stopOT: () => {},
			startupOpentokScreenshare: async () => {},
		};
	}

	const meeting = useSelector((s: GRS) => s.SessionReducer.meeting);
	const embed = useSelector((s: GRS) => s.SessionReducer.embed);
	const user = useSelector((s: GRS) => s.SessionReducer.user);
	const token = useSelector((s: GRS) => s.SessionReducer.token);
	const watcherOnly = useSelector((s: GRS) => s.SessionReducer.watcher_only);
	const companionMode = useSelector((s: GRS) => s.SessionReducer.companionMode);
	const webrtcID = useSelector((s: GRS) => s.SessionReducer.webrtcID);
	const programView = useSelector((s: GRS) => s.SessionReducer.programView);
	const expectIosStream = useSelector((s: GRS) => s.SessionReducer.expectIosStream);
	const restrictedUserState = useSelector((s: GRS) => s.SessionReducer.restrictedUserState);
	const connectedUsers = useSelector((s: GRS) => s.SessionReducer.connectedUsers);
	const screenShareWebrtcId = useSelector((s: GRS) => s.SessionReducer.screenShareWebrtcId);
	const limitedInput = useSelector((s: GRS) => s.SessionReducer.limitedInput);
	const selectedVideo = useSelector((s: GRS) => s.VideoReducer.selectedVideo);
	const selectedAudio = useSelector((s: GRS) => s.VideoReducer.selectedAudio);
	const selectedOutput = useSelector((s: GRS) => s.VideoReducer.selectedOutput);
	const sharingScreen = useSelector((s: GRS) => s.VideoReducer.sharingScreen);
	const mainVideoStream = useSelector((s: GRS) => s.VideoReducer.mainVideoStream);
	const connectedMediaVideos = useSelector((s: GRS) => s.VideoReducer.connectedMediaVideos);
	const connectedPresenterVideos = useSelector((s: GRS) => s.VideoReducer.connectedPresenterVideos);
	const shouldRepublishVideo = useSelector((s: GRS) => s.VideoReducer.shouldRepublishVideo);
	const clearShot = useSelector((s: GRS) => s.SettingsReducer.clearShot);
	const roomState = useSelector((s: GRS) => s.MeetingReducer.roomState);
	const backstageCommsActive = useSelector((s: GRS) => s.SettingsReducer.backstageCommsActive);
	const shots = useSelector((s: GRS) => s.ShotsReducer.shots);
	const currentShotId = useSelector((s: GRS) => s.ShotsReducer.currentShotId);
	const backstageCanvasVolume = useSelector((s: GRS) => s.MeetingReducer.backstageCanvasVolume);
	const unmutedAudioFeeds = useSelector((s: GRS) => s.OpenTokReducer.unmutedAudioFeeds);
	const debugOpentok = useSelector((s: GRS) => s.DebugReducer.debugOpentok);
	const isoArchiveId = useSelector((s: GRS) => s.BroadcastReducer.isoArchiveId);
	const openCommValues = useSelector((s: GRS) => s.CommToggleReducer.entityValue);
	const showDebugUI = useSelector((s: GRS) => s.OpenTokReducer.showDebugUI);
	const opentokSession = useSelector(selectOpentokSession);
	const onsiteLocations = useSelector((s: GRS) => s.VideoReducer.onsiteLocations);
	const onsiteLocationStreams = useSelector((s: GRS) => s.VideoReducer.onsiteLocationStreams);
	const onsiteLocationSession = useSelector((s: GRS) => s.VideoReducer.onsiteLocationSession);
	const videoDevices = useSelector((s: GRS) => s.VideoReducer.videoDevices);
	const isSynergyskySession = useSelector((s: GRS) => s.SessionReducer.isSynergyskySession);
	const screenshareVideoSource = useSelector((s: GRS) => s.VideoReducer.screenshareVideoSource);
	const {
		onsiteUsers,
		advancedAudioUsers,
		r1080pUsers,
	} = useSelector((s: GRS) => s.SessionReducer.userMeetingSettings);

	const {
		setAV,
		removeAV,
		getAV,
	} = useContext(AVContext);

	const { audio, video } = getAV();

	const opentok = useRef<OpenTok | null>(null);
	const opentokElements = useRef<HTMLVideoElement[]>([]);
	const opentokId = useRef<string>('');
	const publishAttempts = useRef<number>(0);
	const startedUp = useRef<boolean>(false);
	const mapOfOpenTokStreamsAttemptedReconnects = useRef<Map<string, number> >(new Map());
	const logOTStatsCountdown = useRef<NodeJS.Timeout>();
	const logOTStatsInterval = useRef<NodeJS.Timeout>();
	const opentokAudioMonitor = useRef<NodeJS.Timeout>();
	const screenshareTrack = useRef<MediaStreamTrack | null>(null);
	const screenshareAudioTrack = useRef<MediaStreamTrack | null>(null);
	const screenshareAudioMuted = useRef<boolean>(false);
	const registeredArchiveStreams = useRef<Set<string>>(new Set());

	const [forceFeedsTabRefresh, setForceFeedsTabRefresh] = useState<boolean>(false);
	const [opentokInitialized, setOpentokInitialized] = useState<boolean>(false);
	const [shouldLogOTStats, setShouldLogOTStats] = useState<boolean>(false);

	const currentShot = useMemo(
		() => shots.find((shot: Shot) => shot.shot_id === currentShotId) ?? shots[0],
		[currentShotId, shots],
	);

	const {
		shot_data: {
			shotFeeds,
		},
	} = currentShot;

	const isOnsitePublisher = useMemo(
		() => onsiteLocationSession?.route === OnsiteLocationSessionRoute.Publishing,
		[onsiteLocationSession],
	);

	const isOnsiteLimitedInput = useMemo(
		() => (
			onsiteLocationSession?.route === OnsiteLocationSessionRoute.LimitedInputCanvas
			&& !!onsiteLocationSession?.id
		),
		[onsiteLocationSession],
	);

	const canvasWebRTCIds = useMemo((): string[] => {
		const newIdsOfVideosInCanvas: string[] = [];
		const len = shotFeeds.length;

		for (let i = 0; i < len; i += 1) {
			const feed = shotFeeds[i];
			if (feed.media) {
				const connectedVideoForMedia = connectedMediaVideos.find(
					(vid: VideoStream) => vid.mediaId === feed.media,
				);
				if (connectedVideoForMedia) {
					newIdsOfVideosInCanvas.push(connectedVideoForMedia.id);
				}
			}

			if (feed.presenter) {
				const connectedVideoForPresenter = connectedPresenterVideos.find(
					(vid: VideoStream) => vid.profile === feed.presenter,
				);
				if (connectedVideoForPresenter) {
					newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
				}
			}

			if (feed.screenshare) {
				const connectedUser = connectedUsers
					.find((_connectedUser) => _connectedUser.email === feed.screenshare);

				if (connectedUser) {
					const connectedVideoForPresenter = connectedPresenterVideos.find(
						(vid: VideoStream) => vid.id === connectedUser.screenShareWebrtcId,
					);

					if (connectedVideoForPresenter) {
						newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
					}
				}
			}

			if (feed.sipStreamId) {
				const connectedVideoForPresenter = connectedPresenterVideos.find(
					(vid: VideoStream) => vid.id === feed.sipStreamId,
				);
				if (connectedVideoForPresenter) {
					newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
				}
			}

			if (feed.onsiteLocationId) {
				const videosForLocations = onsiteLocationStreams.get(feed.onsiteLocationId) ?? [];
				videosForLocations.forEach((connectedLocationStreams: OnsiteLocationStream) => {
					newIdsOfVideosInCanvas.push(connectedLocationStreams.id);
				});
			}

			if (!feed.presenter && feed.email) {
				const connectedUser = connectedUsers
					.find((_connectedUser) => _connectedUser.email === feed.email);

				if (connectedUser) {
					const connectedVideoForPresenter = connectedPresenterVideos.find(
						(vid: VideoStream) => vid.profile === connectedUser.gr_user,
					);

					if (connectedVideoForPresenter) {
						newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
					}
				}
			}
		}

		return newIdsOfVideosInCanvas;
	}, [connectedMediaVideos, connectedPresenterVideos, connectedUsers, shotFeeds]);

	const isOnCanvas = useMemo(
		() => !!((webrtcID && canvasWebRTCIds.includes(webrtcID.toString()))
			|| (screenShareWebrtcId && canvasWebRTCIds.includes(screenShareWebrtcId.toString()))),
		[canvasWebRTCIds, webrtcID],
	);

	const backstageCanvasVolumeOn = useMemo(
		() => !embed && !isOnCanvas && roomState !== ROOM_STATES.EDITING,
		[embed, isOnCanvas, roomState],
	);

	const useAdvancedAudio = useMemo(
		() => (isOnsitePublisher || isOnsiteLimitedInput
			? onsiteLocations.find((l) => l.id === onsiteLocationSession?.id)?.advancedAudio ?? false
			: advancedAudioUsers?.includes(user?.gr_user ?? 0)),
		[isOnsitePublisher, isOnsiteLimitedInput, onsiteLocations, advancedAudioUsers, user],
	);
	const prevUseAdvancedAudio = usePrevious(useAdvancedAudio);

	const useEnable1080p = useMemo(
		() => (isOnsitePublisher || isOnsiteLimitedInput
			? onsiteLocations.find((l) => l.id === onsiteLocationSession?.id)?.resolution1080p ?? false 
			: r1080pUsers?.includes(user?.gr_user ?? 0)),
		[r1080pUsers, user, isOnsitePublisher, isOnsiteLimitedInput, onsiteLocations],
	);
	const prevUseEnable1080p = usePrevious(useEnable1080p);

	const feedQuality = useEnable1080p
		? { width: 1920, height: 1080, frameRate: 30 }
		: { width: 1280, height: 720, frameRate: 30 };

	const setOT = (newOpentok: any): void => {
		opentok.current = newOpentok;
	};

	const resetOT = () => {
		opentok.current = null;
		opentokElements.current = [];
		opentokId.current = '';
		publishAttempts.current = 0;
		startedUp.current = false;
		mapOfOpenTokStreamsAttemptedReconnects.current = new Map();
		registeredArchiveStreams.current = new Set();

		if (socket?.listeners && socket.listeners.has('backstage-comms-toggle')) {
			socket?.removeListener('backstage-comms-toggle');
		}
	};

	const videoFeedError = () => {
		setForceFeedsTabRefresh(true);
		setTimeout(() => setForceFeedsTabRefresh(false), 16);
	};

	const updateWebRTCId = (webRTCId: string | number) => {
		dispatch(setWebrtcId(webRTCId));
		opentokId.current = String(webRTCId) ?? '';
		dispatch(updateLocalId(webRTCId));
	};

	const getOnsiteLocationId = (stream: Stream): number | undefined => {
		const tokenData = JSON.parse(stream.connection.data);
		return tokenData && tokenData.onsiteLocationId;
	};

	const streamIsSip = (stream: Stream) => {
		const tokenData = JSON.parse(stream.connection.data);
		return tokenData && tokenData['sip'] === true;
	};

	const getSipConnectionName = (stream: Stream) => {
		const tokenData = JSON.parse(stream.connection.data);
		return tokenData && tokenData['sip'] === true && tokenData['name'];
	};

	const getPoster = (stream: Stream) => {
		const tokenData = JSON.parse(stream.connection.data);
		return tokenData && tokenData['sip'] === true && tokenData['image'];
	};

	const getSipUserIdentity = (stream: Stream): { gr_user?: number; isPhoneCall?: boolean } => {
		const tokenData = JSON.parse(stream.connection.data);
		if (!tokenData || tokenData['sip'] !== true) return {};
		return {
			gr_user: tokenData['gr_user'] ?? undefined,
			isPhoneCall: !!tokenData['phoneCall'],
		};
	};

	const updateMediaStream = <T extends VideoStream | OnsiteLocationStream>(
		currentVideos: T[],
		newEl: HTMLVideoElement,
	) => {
		const currentStream = currentVideos.find((s) => s.id === newEl.id);
		if (currentStream && currentStream.srcObject !== newEl.srcObject) {
			const updatedStreams = currentVideos.map((stream) => {
				if (stream.id === newEl.id) {
					stream.srcObject = newEl.srcObject as MediaStream;
				}
				return stream;
			});
			return updatedStreams;
		}
		return null;
	};

	// https://api.support.vonage.com/hc/en-us/articles/10901330247964-How-to-update-subscriber-stream-when-manipulating-video-element
	// Due to the introduction of Adaptive Media Routing in OpenTok 2.24.7,
	// subscriber streams may be updated depending on how many users are connected to the session.
	// The following is a modified version of Vonage's recommended way of handling this for custom UI:
	const checkStreamOnPlayback = ({ target }: Event, onsiteLocationId: number | undefined) => {
		if (!target || !(target instanceof HTMLVideoElement)) return;
		const {
			VideoReducer: {
				onsiteLocationStreams: locationStreams,
				connectedPresenterVideos: currentPresenterVideos,
			},
		} = store.getState() as GRS;

		if (onsiteLocationId) {
			const currentLocationStreams = locationStreams.get(onsiteLocationId) ?? [];
			const newStreams = updateMediaStream(currentLocationStreams, target);
			if (newStreams) {
				dispatch(setOnsiteLocationStreams(onsiteLocationId, newStreams));
			}
		} else {
			const newStreams = updateMediaStream(currentPresenterVideos, target);
			if (newStreams) {
				dispatch(setConnectedPresenterVideos(newStreams));
			}
		}
	};

	const onVideoElementCreated = (event: any, isPublisher: boolean) => {
		if (isPublisher) {
			dispatch(setMainVideoStream(event.element.srcObject));
		} else {
			// This fires when a user joins the same opentok session we are connected to.
			// Our client will subscribe to the stream that they're publishing through opentok.
			const {
				VideoReducer,
				SessionReducer,
			} = store.getState() as GRS;

			const handleStream = (stream: Stream) => {
				const {
					streamId,
					name: streamNameOG,
				} = stream;

				// The stream name is empty for sip calls, adding a random number and sip to the end
				// If the stripped string contains an existing userId it can complicate things.
				// Though that is a low probability.  The stripped string is consistent across
				// users, so drag and dropping works but one must re-drag drop a sip user if they
				// if they leave and are re-called.
				const stripped = streamId?.replace(/\D/g, '');
				const streamName = streamNameOG || `${stripped},sip`;
				if (!streamId || !streamName) {
					return;
				}

				const isSynergySkyScreenshare = streamName.includes('synergysky-screen') || streamName.includes('secondary-screen');
				const isScreenshare = streamIsScreenshare(event.target) || isSynergySkyScreenshare;
				const isMobile = streamIsMobile(stream);
				const onsiteLocationId = getOnsiteLocationId(stream);

				const targetProfile = isMobile
					? Number(getUserIdFromMobileStream(stream))
					: Number(streamName.split(',')[0]);

				if (streamIsSip(stream)) {
					console.log('[handleStream] SIP stream', {
						streamId,
						streamNameOG,
						streamName,
						targetProfile,
						hasAudio: stream.hasAudio,
						hasVideo: stream.hasVideo,
						isNaN: Number.isNaN(targetProfile),
						isPhoneCall: getSipUserIdentity(stream).isPhoneCall,
					});
				}

				if ((targetProfile < 0) || Number.isNaN(targetProfile)) {
					return;
				}

				/*
				* video element updates
				*/
				event.element.id = event.target.streamId;

				// in embed mode, automute anyone who connects (they will be unmuted when put into canvas)
				if (SessionReducer.embed) {
					event.element.volume = 0.0;
				}

				if (VideoReducer.selectedOutput && event.element.srcObject.getAudioTracks()?.[0]) {
					event.element.setSinkId(VideoReducer.selectedOutput?.deviceId);
				}

				event.element.onplay = (e: Event) => checkStreamOnPlayback(e, onsiteLocationId);

				opentokElements.current = [...opentokElements.current, event.element];

				/*
				* reducer updates
				*/
				const tempConnectedVideos = [...VideoReducer.connectedPresenterVideos];
				let isUserJoining = true;

				batch(() => {
					// The sip won't have a targetProfile
					const connectedUser = SessionReducer.connectedUsers
						.find((_connectedUser) => _connectedUser.gr_user === targetProfile
							|| _connectedUser.brandlive_profile === targetProfile);

					if (connectedUser) {
						if (targetProfile === SessionReducer.user?.gr_user) {
							dispatch(addFeedWebRTCId(streamId));
						}

						// sad reverse compatibility things
						if (isScreenshare) {
							connectedUser.screenShareWebrtcId = streamId;

							if (connectedUser.feedWebRTCIds && !connectedUser.feedWebRTCIds.includes(streamId)) {
								connectedUser.feedWebRTCIds.push(streamId);
							}

							if (targetProfile === SessionReducer.user?.gr_user) {
								dispatch(setScreenShareWebrtcId(streamId));
							}
						}

						dispatch(userUpdated(connectedUser));
					}

					if (onsiteLocationId) {
						dispatch(addOnsiteLocationStream(
							onsiteLocationId,
							{
								id: streamId,
								srcObject: event.element.srcObject,
							},
						));
						return;
					}

					for (let i = 0; i < tempConnectedVideos.length; i += 1) {
						// if the connecting user already exists in connected presenters, just update
						if (
							(isScreenshare && tempConnectedVideos[i].id === streamId)
							|| (!isScreenshare && tempConnectedVideos[i].profile === targetProfile)
						) {
							tempConnectedVideos[i].srcObject = event.element.srcObject;
							// Adopt the new stream id — on a republish streamCreated lands before the old
							// streamDestroyed, whose removePresenterVideo(oldId) would delete this live feed.
							tempConnectedVideos[i].id = streamId;
							tempConnectedVideos[i].videoMuted = stream.hasVideo;
							tempConnectedVideos[i].muted = stream.hasAudio;
							tempConnectedVideos[i].mutedPreference = stream.hasAudio;
							tempConnectedVideos[i].profile = isScreenshare ? 0 : targetProfile;
							tempConnectedVideos[i].isMobile = isMobile;
							tempConnectedVideos[i].isScreenShare = isScreenshare;
							isUserJoining = false;
							break;
						}
					}

					if (isUserJoining) {
						// New user joining
						const isSip = streamIsSip(stream);
						const sipIdentity = isSip ? getSipUserIdentity(stream) : {};
						dispatch(addPresenterVideo({
							srcObject: event.element.srcObject,
							id: streamId,
							videoMuted: stream.hasVideo,
							muted: stream.hasAudio,
							mutedPreference: stream.hasAudio,
							profile: isScreenshare ? 0 : (sipIdentity.gr_user ?? targetProfile),
							isMobile,
							isScreenShare: isScreenshare,
							isSipCall: isSip,
							isPhoneCall: sipIdentity.isPhoneCall,
							displayName: isSip ? getSipConnectionName(stream) : undefined,
							poster: getPoster(stream),
						}));
					} else {
						// No new users, just updates
						dispatch(setConnectedPresenterVideos(tempConnectedVideos));
					}
				});
			};

			if (event.target instanceof Subscriber) {
				handleStream(event.target.stream);
			} else if (event.target instanceof Publisher) {
				(event.target as Publisher).once('streamCreated', (streamEvent) => {
					handleStream(streamEvent.stream);
				});
			}
		}
	};

	const handlePublisherDestroyed = async (e: any) => {
		const {
			VideoReducer,
			SessionReducer,
		} = store.getState() as GRS;

		const stillPublishingVideo = VideoReducer.connectedPresenterVideos.find(
			(vid) => vid.id === e.stream.id,
		);

		if (stillPublishingVideo || isOnsitePublisher || isOnsiteLimitedInput) {
			const webRTCId = e.stream.id ?? '';
			const connectedUser = SessionReducer.connectedUsers
				.find((_connectedUser) => _connectedUser.webrtcID === webRTCId);

			if (connectedUser) {
				dispatch(removeFeedWebRTCId(webRTCId));

				if (isOnsitePublisher || isOnsiteLimitedInput) {
					connectedUser.webrtcID = '';
					updateWebRTCId('');
					dispatch(userUpdated(connectedUser));
				}
			}

			if (stillPublishingVideo) {
				dispatch(removePresenterVideo(webRTCId));
			}
		}

		if (opentok.current?.publisher && opentok.current?.publisher.streamId === e.target.streamId) {
			opentok.current.publisher = null;
		}
	};

	const initTokbox = () => new Promise(async (resolve, reject) => {
		try {
			if (opentokSession) {
				const tokboxToken = await GetTokboxToken(
					opentokSession,
					isOnsitePublisher || isOnsiteLimitedInput
						? { onsiteLocationId: onsiteLocationSession?.id }
						: undefined,
				);

				if (!tokboxToken?.token) {
					Logger.log({ opentok_token_failed: {
						sessionId: opentokSession,
						status: typeof tokboxToken === 'number' ? tokboxToken : undefined,
					} });
				}

				if (tokboxToken) {
					opentok.current = new OpenTok(
						user,
						opentokSession,
						tokboxToken.token,
						setAV,
						removeAV,
						onVideoElementCreated,
						(openTokStats: TotalOTStats) => dispatch(setOpenTokStats(openTokStats)),
						handlePublisherDestroyed,
						debugOpentok,
						embed,
					);
					Logger.log({ opentok_connected: { sessionId: opentokSession } });
					resolve(true);
				} else {
					throw new Error('Unable to get Tokbox token');
				}
			} else {
				resolve(false);
			}
		} catch (e) {
			console.error(e);
			reject(e);
		}
	});

	// see comment for "backstage comms" above
	// this is a temp. stream unmute for the producer to talk to presenters who are in the shot
	// (or a single presenter, if "targetedPresenterID" is specified)
	const applyBackstageComms = (
		incoming: BackstageCommsSocketMessage,
		newIdsOfVideosInCanvas: string[],
		shotIsCleared: boolean,
		myWebRTCId: string,
	) => {
		const { producerID, targetedPresenterID } = incoming.message;
		let { muted } = incoming.message;
		// if muted is false, the passed producerID (the producer that clicked the button)
		// will have their coresponding opentok element in my app unmuted

		// if im not live, I am backstage, producer is always unmuted
		if (!newIdsOfVideosInCanvas.includes(myWebRTCId?.toString() ?? '')) {
			muted = false;
		}

		// if session & producer are both live on canvas, producer always unmuted
		if (
			newIdsOfVideosInCanvas.includes(myWebRTCId?.toString() ?? '')
			&& newIdsOfVideosInCanvas.includes(producerID)
		) {
			muted = false;
		}

		// if clearShot is true, there is no bsc, force unmute
		if (shotIsCleared) {
			muted = false;
		}

		// session received msg & specific producer id should be muted/unmuted,
		// loop to find a link between producer-publisher webrtcid
		const numberOfStreams = opentokElements.current.length;

		let ii = 0;
		for (ii; ii < numberOfStreams; ii += 1) {
			const streamId = opentokElements.current[ii].id;
			// if this opentok element belongs to producer
			if (streamId === producerID) {
				// if <muted> for everyone or just for me
				if (!targetedPresenterID || targetedPresenterID === myWebRTCId) {
					opentokElements.current[ii].volume = muted ? 0.0 : 1.0;
					if (opentok.current?.subscribers[streamId]) {
						// if this is a subscriber, set the audio volume
						opentok.current.subscribers[streamId].setAudioVolume(muted ? 0 : 100);
					}
				}
			}
		}
	};

	// Identity a stream needs for ISO naming/status (gr_opentok_archive_streams)
	const deriveArchiveStreamInfo = (
		stream: Stream,
		source: ArchiveStreamSource,
	): ArchiveStreamInfo | null => {
		const streamName = stream.name || '';
		const connData = parseStreamConnectionData(stream);
		const isSip = connData?.sip === true;
		const onsiteLocationId: number | undefined = connData?.onsiteLocationId;
		const isScreenshare = isStreamScreenshare(stream);

		let grUser: number | undefined;
		let deviceType: string = CONNECTED_PRESENTER_VIDEO_TYPES.CAMERA;
		let name: string | undefined;

		if (isSip) {
			name = connData?.name;
		} else if (onsiteLocationId != null && onsiteLocationId !== 0) {
			name = source.onsiteLocations
				.find((location) => location.id === onsiteLocationId)?.locationName;
		} else if (isScreenshare) {
			deviceType = CONNECTED_PRESENTER_VIDEO_TYPES.SCREENSHARE;
			const profile = getProfileFromStreamName(streamName);
			grUser = source.connectedUsers.find(
				(u) => u.gr_user === profile || u.brandlive_profile === profile,
			)?.gr_user;
		} else {
			const profile = getProfileFromStreamName(streamName);
			if (!Number.isNaN(profile) && profile > 0) {
				grUser = profile;
			}
		}

		// No identity resolved. Registering anyway would write a row with a null gr_user and
		// name, and because the insert has no update path that row is permanent.
		// Returning null also leaves the stream uncached, so the sweep retries it once its
		// user or location arrives.
		if (!grUser && !name) {
			return null;
		}

		return {
			streamId: stream.streamId,
			grUser,
			deviceType,
			name,
		};
	};

	const archiveStreamSourceFromStore = (): ArchiveStreamSource => {
		const {
			SessionReducer,
			BroadcastReducer,
			VideoReducer,
		} = store.getState() as GRS;

		return {
			isoArchiveId: BroadcastReducer.isoArchiveId,
			embed: SessionReducer.embed,
			token: SessionReducer.token,
			meetingId: SessionReducer.meeting?.meeting,
			connectedUsers: SessionReducer.connectedUsers,
			onsiteLocations: VideoReducer.onsiteLocations,
		};
	};

	// Registers one stream for ISO naming/status. Called for every stream in the session
	// Idempotent per archive, so the join path and the archive-start sweep can safely overlap.
	const registerArchiveStream = async (stream: Stream, source: ArchiveStreamSource) => {
		const sessionId = opentok.current?.session?.sessionId;

		if (
			!source.isoArchiveId
			|| !source.embed
			|| !source.token
			|| !source.meetingId
			|| !sessionId
		) {
			return;
		}

		const info = deriveArchiveStreamInfo(stream, source);

		if (!info) return;

		const registrationKey = `${source.isoArchiveId}:${info.streamId}`;

		if (registeredArchiveStreams.current.has(registrationKey)) return;

		// Claim the key up front so an overlapping sweep does not post the same stream twice.
		registeredArchiveStreams.current.add(registrationKey);

		const registered = await SetStreamInfo(
			source.token,
			info.streamId,
			source.isoArchiveId,
			sessionId,
			info.grUser,
			source.meetingId,
			info.deviceType,
			info.name,
		);

		// Release the key when the write did not land, so the next sweep retries it rather
		// than treating a failed request as registered for the rest of the recording.
		if (!registered) {
			registeredArchiveStreams.current.delete(registrationKey);
		}
	};

	const streamAdded = async (event: any) => {
		const {
			SessionReducer,
			ShotsReducer,
		} = store.getState() as GRS;

		try {
			if (event?.stream?.streamId) {
				registerArchiveStream(event.stream, archiveStreamSourceFromStore());

				// Diagnostic: log SIP/phone call streams so we can confirm they arrive
				const connData = parseStreamConnectionData(event.stream);
				const isSipStream = connData?.sip === true;
				const isOnsiteLocationStream = connData?.onsiteLocationId != null
					&& connData.onsiteLocationId !== 0;
				if (connData?.sip) {
					console.log('[streamAdded] SIP stream received', {
						streamId: event.stream.streamId,
						streamName: event.stream.name,
						hasAudio: event.stream.hasAudio,
						hasVideo: event.stream.hasVideo,
						isPhoneCall: !!connData.phoneCall,
						connectionData: connData,
					});
				}

				// Embed mode: only subscribe to feeds present in the current or
				// next shot. Skipped streams stay tracked in opentok.streams and
				// use-resolution-update will subscribe when shots change.
				if (SessionReducer.embed) {
					const streamName = event.stream.name || '';
					const isScreenshareStream = isStreamScreenshare(event.stream);

					const currentShotForGate = ShotsReducer.shots.find(
						(s: Shot) => s.shot_id === ShotsReducer.currentShotId,
					) ?? ShotsReducer.shots[0];

					if (currentShotForGate) {
						const currentFeeds = currentShotForGate.shot_data.shotFeeds;
						const currentOrder = currentShotForGate.shot_data.order;
						const nextShotForGate = ShotsReducer.shots.find(
							(s: Shot) => s.shot_data.order === currentOrder + 1,
						);
						const nextFeeds = nextShotForGate?.shot_data.shotFeeds ?? [];
						const bothFeeds = [...currentFeeds, ...nextFeeds];

						let isNeeded = false;

						if (isSipStream) {
							isNeeded = bothFeeds.some(
								(f: ShotFeed) => f.sipStreamId === event.stream.streamId,
							);
						} else if (isOnsiteLocationStream) {
							const streamLocationId: number | undefined = connData?.onsiteLocationId;
							isNeeded = streamLocationId != null && streamLocationId !== 0
								&& bothFeeds.some(
									(f: ShotFeed) => f.onsiteLocationId === streamLocationId,
								);
						} else if (isScreenshareStream) {
							const profile = getProfileFromStreamName(streamName);
							const screenshareUser = SessionReducer.connectedUsers.find(
								(u) => u.gr_user === profile
									|| u.brandlive_profile === profile,
							);
							isNeeded = !!screenshareUser && bothFeeds.some(
								(f: ShotFeed) => f.screenshare === screenshareUser.email,
							);
						} else {
							const presenterProfile = getProfileFromStreamName(streamName);

							if (!Number.isNaN(presenterProfile) && presenterProfile > 0) {
								isNeeded = bothFeeds.some(
									(f: ShotFeed) => f.presenter === presenterProfile,
								);
							} else {
								isNeeded = true;
							}
						}

						if (!isNeeded) {
							return;
						}
					}
				}

				let attemptsForThisStream = mapOfOpenTokStreamsAttemptedReconnects.current.get(
					event.stream.streamId,
				);

				if (attemptsForThisStream) {
					attemptsForThisStream += 1;
				} else {
					attemptsForThisStream = 1;
				}

				mapOfOpenTokStreamsAttemptedReconnects.current.set(
					event.stream.streamId,
					attemptsForThisStream,
				);

				if (attemptsForThisStream < 11) {
					// remove mobile audio if stream is from self since opentok sees
					// them as two different devices and won't cancel the audio
					if (SessionReducer.user
						&& streamIsMobile(event.stream)
						&& isLocalUser(
							SessionReducer.user,
							getUserIdFromMobileStream(event.stream),
						)
					) {
						event.stream.channel = event.stream.channel.filter(
							(channel_: any) => channel_.type !== 'audio',
						);
					}

					// allow reconnection attempts while preventing repeat errors
					if (event.stream && !event.stream?.destroyed) {
						await opentok.current?.subscribeToStream(
							event.stream,
							SessionReducer.companionMode,
							SessionReducer.embed,
						);
					} else {
						setTimeout(async () => {
							await streamAdded(event);
						}, 1000 * attemptsForThisStream);
					}
				}
			}
		} catch (e) {
			console.error(e);

			setTimeout(async () => {
				await streamAdded(event);
			}, 1000);
		}
	};

	const streamRemoved = async (event: any) => {
		const {
			VideoReducer,
			SessionReducer,
		} = store.getState() as GRS;

		try {
			const webRTCId = event.stream.id;

			// get this connected video
			const connectedVideo = VideoReducer.connectedPresenterVideos
				.find((vid: VideoStream) => vid.id === webRTCId);

			dispatch(removePresenterVideo(webRTCId));

			opentokElements.current = opentokElements.current.filter(
				(el: HTMLVideoElement) => !!el.srcObject,
			);

			const onsiteLocationId = getOnsiteLocationId(event.stream);
			if (onsiteLocationId) {
				dispatch(removeOnsiteLocationStream(
					onsiteLocationId,
					webRTCId,
				));
				return;
			}

			if (connectedVideo && connectedVideo.isMobile) {
				const connectedUser = SessionReducer.connectedUsers
					.find((_connectedUser) => _connectedUser.webrtcID
						&& _connectedUser.webrtcID === connectedVideo?.id);

				if (connectedUser) {
					connectedUser.in_room = false;
					if (connectedUser.gr_user === SessionReducer.user?.gr_user) {
						dispatch(removeFeedWebRTCId(webRTCId));
					}
					dispatch(userUpdated(connectedUser));
				}
			}

			// if this was a mobile connection and the connection was from this
			// user and this user is not just a watcher, startup the connected video stream
			if (
				user
				&& connectedVideo?.isMobile
				&& SessionReducer.embed
				&& connectedVideo?.profile === user.gr_user
				&& SessionReducer.watcher_only
				&& (VideoReducer.selectedVideo || VideoReducer.selectedAudio)
			) {
				await opentok.current?.startStream(
					audio,
					video,
					VideoReducer.selectedAudio?.deviceId,
					VideoReducer.selectedVideo?.deviceId,
					String(user.gr_user),
					true,
					feedQuality,
					useAdvancedAudio,
				);

				opentok.current?.setLocalVideo(connectedVideo.videoMuted || false);
				opentok.current?.setLocalAudio(connectedVideo.muted || false);

				if (opentok.current?.publisher?.streamId) {
					updateWebRTCId(opentok.current.publisher.streamId);
				}

				const connectedUser = SessionReducer.connectedUsers
					.find((_connectedUser) => _connectedUser.webrtcID
						&& _connectedUser.webrtcID === connectedVideo?.id);

				if (connectedUser) {
					connectedUser.in_room = false;
					if (connectedUser.gr_user === SessionReducer.user?.gr_user) {
						dispatch(removeFeedWebRTCId(webRTCId));
						if (opentok.current?.publisher?.streamId) {
							dispatch(addFeedWebRTCId(String(opentok.current?.publisher?.streamId)));
						}
					}

					dispatch(userUpdated(connectedUser));
				}
				dispatch(addPresenterVideo({
					...connectedVideo,
					id: opentok.current?.publisher?.streamId,
					isMobile: false,
					srcObject: VideoReducer.mainVideoStream,
				}));
			}
		} catch (e: any) {
			if (SessionReducer.token) {
				ReportError(
					SessionReducer.token,
					window.navigator.userAgent,
					e.stack,
					e.message,
				).catch((err) => {
					console.error(err);
				});
			}
			console.error(e);
		}
	};

	const handleReconnect = () => {
		// Prevent the stale audio monitor from firing setShouldRepublishVideo(true)
		// repeatedly while opentok.current is null during reconnect.
		clearInterval(opentokAudioMonitor.current);

		// Capture the live screen capture tracks before destroying the instance so we
		// can reuse them on reconnect without re-triggering the browser picker.
		if (sharingScreen) {
			screenshareTrack.current = opentok.current?.getScreenshareTrack() ?? null;
			screenshareAudioTrack.current = opentok.current?.getScreenshareAudioTrack() ?? null;
			// stream.hasAudio is false when the screenshare audio was muted by the producer.
			screenshareAudioMuted.current = opentok.current?.screensharePublisher?.stream?.hasAudio === false;
		}

		const {
			VideoReducer,
			SessionReducer,
		} = store.getState() as GRS;

		const userVideo = {
			id: 'local',
			srcObject: VideoReducer.mainVideoStream,
			videoMuted: VideoReducer.videoMuted,
			muted: VideoReducer.audioMuted,
			mutedPreference: VideoReducer.audioMuted,
			userInitiated: VideoReducer.audioMuted,
			self: true,
			local: true,
			profile: SessionReducer.user?.gr_user ?? 1,
			brandliveProfileId: SessionReducer.user?.brandlive_profile,
			isMedia: false,
		};

		opentok.current?.stop();
		opentok.current = null;
		if (!isOnsitePublisher) {
			dispatch(setConnectedPresenterVideos(isOnsiteLimitedInput ? [] : [userVideo]));
			dispatch(resetOnsiteLocationStreams());
		}
		dispatch(clearFeedWebRTCIds());
		videoFeedError();
	};

	const handleScreensharingStop = useCallback(() => {
		const connectedUser = connectedUsers.find(
			(_connectedUser) => _connectedUser.gr_user === user?.gr_user
		);

		if (connectedUser?.screenShareWebrtcId) {
			dispatch(removeFeedWebRTCId(String(connectedUser.screenShareWebrtcId)));
			dispatch(userUpdated({ ...connectedUser, screenShareWebrtcId: '' }));
		}

		dispatch(setSharingScreen(false));
		dispatch(setScreenShareWebrtcId(''));
	}, [dispatch, connectedUsers, user]);

	// Keep a ref to the latest handleScreensharingStop so the device-initiated
	// GetDisplayMedia handler always calls the current version without re-registering.
	const handleScreensharingStopRef = useRef(handleScreensharingStop);
	useEffect(() => {
		handleScreensharingStopRef.current = handleScreensharingStop;
	}, [handleScreensharingStop]);

	// Device-initiated screenshare for SynergySky sessions.
	// The SynergySky SDK overrides navigator.mediaDevices.getDisplayMedia() so that
	// calling it inside the READY event handler returns the device's secondary video
	// stream directly — no camera lookup or browser picker needed.
	useEffect(() => {
		if (!isSynergyskySession) return;

		window.synergysky?.addEventHandler('GetDisplayMedia', async (_event, action) => {
			if (action === window.synergysky?.GetDisplayMediaEventTypes.READY) {
				try {
					const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
					const videoTrack = stream.getVideoTracks()[0];
					if (!videoTrack || !opentok.current) return;

					await opentok.current.startScreensharing(
						handleScreensharingStopRef.current,
						videoTrack,
						`${user?.gr_user},secondary-screen`,
					);
					dispatch(setSharingScreen(true));

					const screenshareStream = opentok.current.screensharePublisher?.stream;
					if (screenshareStream) {
						registerArchiveStream(screenshareStream, archiveStreamSourceFromStore());
					}
				} catch (e: any) {
					console.error('[SynergySky] getDisplayMedia error:', e);
					if (token) {
						ReportError(token, window.navigator.userAgent, e.stack, e.message).catch(console.error);
					}
				}
			} else if (action === window.synergysky?.GetDisplayMediaEventTypes.STOPPED) {
				handleScreensharingStopRef.current();
			}
		});
	}, [isSynergyskySession]);

	const prevScreenshareVideoSource = usePrevious(screenshareVideoSource);

	useEffect(() => {
		const switchScreenshareSource = async () => {
			if (
				sharingScreen
				&& screenshareVideoSource !== prevScreenshareVideoSource
				&& opentok.current?.screensharePublisher
			) {
				// We must remove the listeners for the previous screenshare
				// otherwise the stopScreensharing call will trigger the stop handler
				// and set sharingScreen to false
				opentok.current.screensharePublisher.off('mediaStopped streamDestroyed', handleScreensharingStop);
				opentok.current.stopScreensharing();
				await startupOpentokScreenshare();
			}
		};

		switchScreenshareSource();
	}, [screenshareVideoSource, prevScreenshareVideoSource, sharingScreen, handleScreensharingStop]);

	const startupOpentokScreenshare = async (existingTrack?: MediaStreamTrack) => {
		try {
			const connectedUser = connectedUsers
				.find((_connectedUser) => _connectedUser.gr_user === user?.gr_user);
			const canScreenshare = await opentok.current?.checkScreenshare();

			// On reconnect (existingTrack provided), startup() already confirmed the session is
			// connected — skip the connectedUser guard which may not be satisfied yet due to socket
			// state not having caught up after the session switch.
			if ((!existingTrack && !connectedUser) || !canScreenshare) {
				return;
			}

			if (screenshareVideoSource) {
				await opentok.current?.startScreensharing(
					handleScreensharingStop,
					screenshareVideoSource,
					`${user?.gr_user},secondary-screen`,
				);
			} else if (isSynergyskySession) {
				// Screenshare is device-initiated via the GetDisplayMedia event handler.
				return;
			} else if (existingTrack) {
				// Pass existing track on reconnect to skip the browser picker; secondary-screen name
				// ensures detection works since contentHint alone won't set channel[0].source = 'screen'.
				await opentok.current?.startScreensharing(
					handleScreensharingStop,
					existingTrack,
					`${user?.gr_user},secondary-screen`,
					screenshareAudioTrack.current ?? undefined,
				);
				screenshareAudioTrack.current = null;
				// Restore the muted state from before the session switch.
				if (screenshareAudioMuted.current) {
					opentok.current?.screensharePublisher?.publishAudio(false);
				}
			} else {
				// No manual source, no existing track — trigger the browser's native screen picker.
				await opentok.current?.startScreensharing(handleScreensharingStop);
			}

			// Like the camera publisher, a screenshare we publish never fires session
			// streamCreated, so nothing else would register it.
			const screenshareStream = opentok.current?.screensharePublisher?.stream;
			if (screenshareStream) {
				registerArchiveStream(screenshareStream, archiveStreamSourceFromStore());
			}
		} catch (e: any) {
			console.error(e);

			dispatch(setSharingScreen(false));

			if (token) {
				ReportError(
					token,
					window.navigator.userAgent,
					e.stack,
					e.message,
				).catch((e2) => {
					console.error(e2);
				});
			}

			showAlert({
				message: 'Screen share error',
				description: 'We ran into an issue capturing your screen. Please try again.',
				duration: 5000,
				type: 'error',
			});
		}
	};

	const startup = async () => {
		try {
			startedUp.current = true;

			if (!isOnsitePublisher) {
				opentok.current?.onStreamAdded(streamAdded);
				opentok.current?.onStreamRemoved(streamRemoved);
				opentok.current?.onReconnected(streamAdded);
			}
			opentok.current?.onSessionDisconnect(handleReconnect);

			// if embedded, don't publish video
			if (
				mainVideoStream
				&& (!embed || (embed && limitedInput)) // limited input canvas does publish
				&& !watcherOnly
				&& !companionMode
				&& !expectIosStream
				&& user
			) {
				const userName = window.location.search.includes('iosEmulate')
					? `mob-${user.gr_user}`
					: String(user.gr_user);
				await opentok.current?.startStream(
					audio,
					video,
					selectedAudio?.deviceId,
					selectedVideo?.deviceId,
					userName,
					false,
					feedQuality,
					useAdvancedAudio,
				);
				const webRTCId = String(opentok.current?.publisher?.streamId);
				updateWebRTCId(webRTCId);
				dispatch(addFeedWebRTCId(webRTCId));
				Logger.log({ opentok_published: { sessionId: opentokSession, streamId: webRTCId } });

				// Own publish never fires session streamCreated, so register it here — only
				// limited-input embeds publish, and their ISO needs a name too.
				const publishedStream = opentok.current?.publisher?.stream;
				if (publishedStream) {
					registerArchiveStream(publishedStream, archiveStreamSourceFromStore());
				}

				const connectedUser = connectedUsers
					.find((_connectedUser) => _connectedUser.gr_user === user?.gr_user);

				if (connectedUser && webRTCId) {
					connectedUser.webrtcID = webRTCId;
					dispatch(userUpdated(connectedUser));
				}

				opentok.current?.setLocalVideo(video);
				opentok.current?.setLocalAudio(audio);

				// Stream published successfully — clear any lingering RECONNECTING state so the
				// reload guard below does not fire after a prior failed attempt that set it.
				if (restrictedUserState === RESTRICTED_USER_STATES.RECONNECTING) {
					dispatch(setRestrictedUserState(RESTRICTED_USER_STATES.NONE));
				}

				if (sharingScreen) {
					await startupOpentokScreenshare(screenshareTrack.current ?? undefined);
					screenshareTrack.current = null;
					screenshareAudioTrack.current = null;
					screenshareAudioMuted.current = false;
				}

				if (RESTRICTED_CONNECTION_TYPES.includes(restrictedUserState)) {
					window.location.reload();
				}
			} else {
				await opentok.current?.subscribeOnly();
			}
		} catch (e: any) {
			if (token) {
				ReportError(
					token,
					navigator.userAgent,
					e.stack,
					e.message,
				).catch((e2) => console.error(e2));
			}

			if (publishAttempts.current < 10) {
				setTimeout(() => {
					if (!opentok.current && programView === ProgramViewType.OnsiteLocation) {
						return; // no longer attempting to publish an onsite location
					}
					opentok.current?.stop();
					opentok.current = null;
					dispatch(setConnectedPresenterVideos([]));
					publishAttempts.current += 1;
					videoFeedError();
					if (restrictedUserState !== RESTRICTED_USER_STATES.RECONNECTING) {
						dispatch(setRestrictedUserState(RESTRICTED_USER_STATES.RECONNECTING));
					}
				}, 3000);
			} else {
				if (restrictedUserState !== RESTRICTED_USER_STATES.BLOCKED) {
					dispatch(setRestrictedUserState(RESTRICTED_USER_STATES.BLOCKED));
				}
				showAlert({
					message: 'Video Error',
					description: 'We ran into an issue publishing your video to the room. Please ensure your camera is connected, no other programs are using it, and you have sufficient bandwidth.',
					type: 'error',
					duration: 5000,
				});
			}
		} finally {
			setOpentokInitialized(true);
		}
	};

	const initialSetup = async () => {
		resetOT();

		if (mainVideoStream && (!watcherOnly && !expectIosStream)) {
			// remove the current main id from arrays
			if (webrtcID) {
				dispatch(removeFeedWebRTCId(String(webrtcID)));
				dispatch(removePresenterVideo(String(webrtcID)));
				dispatch(removeFeedWebRTCId('local'));
				dispatch(removePresenterVideo('local'));
			}

			if (
				!isOnsitePublisher
				&& !isOnsiteLimitedInput
				&& !RESTRICTED_CONNECTION_TYPES.includes(restrictedUserState)
			) {
				dispatch(addPresenterVideo({
					id: 'local',
					srcObject: mainVideoStream,
					videoMuted: !video,
					muted: !audio,
					mutedPreference: !audio,
					userInitiated: !audio,
					self: true,
					local: true,
					profile: user?.gr_user ?? 1,
					brandliveProfileId: user?.brandlive_profile,
				}));
			}

			try {
				await initTokbox();
				startup();
			} catch (e: any) {
				if (token) {
					ReportError(
						token,
						navigator.userAgent,
						e.stack,
						e.message,
					).catch((e2) => console.error(e2));
				}

				if (publishAttempts.current < 10) {
					setTimeout(() => {
						if (!opentok.current && programView === ProgramViewType.OnsiteLocation) {
							return; // no longer attempting to publish an onsite location
						}
						opentok.current?.stop();
						opentok.current = null;
						dispatch(setConnectedPresenterVideos([]));
						publishAttempts.current += 1;
						videoFeedError();

						if (restrictedUserState !== RESTRICTED_USER_STATES.RECONNECTING) {
							dispatch(setRestrictedUserState(RESTRICTED_USER_STATES.RECONNECTING));
						}
					}, 3000);
				} else {
					if (restrictedUserState !== RESTRICTED_USER_STATES.BLOCKED) {
						dispatch(setRestrictedUserState(RESTRICTED_USER_STATES.BLOCKED));
					}
					showAlert({
						message: 'Video Error',
						description: 'We ran into an issue connecting to the room. Please ensure no other programs are using your camera, and you have sufficient bandwidth.',
						type: 'error',
						duration: 5000,
					});
				}
			}
		} else if (!mainVideoStream || embed || watcherOnly || expectIosStream) {
			await initTokbox();
			startup();
		}
	};

	const restartPublisher = async () => {
		if (!opentok.current || !opentok.current?.startStream) {
			console.log('no opentok instance');
			return 0;
		}

		let newPublisher: any;
		try {
			newPublisher = await opentok.current?.startStream(
				audio,
				video,
				selectedAudio?.deviceId,
				selectedVideo?.deviceId,
				undefined,
				true,
				feedQuality,
				useAdvancedAudio,
			);
		} catch (e) {
			// startStream can reject (e.g. NotAllowedError on "never allow"). Old-publisher teardown
			// only runs on success, so the kept-alive feed/state stay intact — swallow it.
			logger.log(`restartPublisher: startStream failed, keeping current state: ${e}`);
			return 0;
		}

		if (!newPublisher) {
			console.log('no publisher exists after startStream attempt');
			return 0;
		}

		if (!newPublisher?.stream) {
			console.log('no stream for publishing attempt');
			return 0;
		}

		if (newPublisher?.streamId === opentokId.current) {
			console.log('this stream has already been published');
			return 0;
		}

		const existingPublisherStreams = opentok.current?.myPublishers
			.map((p) => p.stream)
			.filter((s) => !!s && s.videoType !== 'screen' && s.streamId !== newPublisher?.streamId);

		existingPublisherStreams?.forEach((s) => {
			opentok.current?.stopPublishing(s?.streamId);
			console.log('old publisher exists, destroying it');
		});

		if (newPublisher?.streamId) {
			if (webrtcID) {
				dispatch(removeFeedWebRTCId(String(webrtcID)));
				// Clear the stale self feed before re-adding — the reducer doesn't dedupe self
				// entries, so skipping this leaves a duplicate. Mirrors initialSetup().
				dispatch(removePresenterVideo(String(webrtcID)));
				dispatch(removeFeedWebRTCId('local'));
				dispatch(removePresenterVideo('local'));
			}
			dispatch(addFeedWebRTCId(String(newPublisher?.streamId)));
			updateWebRTCId(newPublisher.streamId);
		}

		// The fresh publisher re-acquired both inputs — clear the stale suspend flags before
		// setLocalVideo below re-checks them, else it would request another republish and loop.
		if (opentok.current) {
			opentok.current.videoSuspended = false;
			opentok.current.audioSuspended = false;
			// Sync the restricted state to the freshly-cleared suspend flags (now NONE). The
			// self-feed thumbnail gates its video-off placeholder on restrictedUserState, so
			// without this it keeps showing the camera-restricted placeholder after a re-grant
			// republish even though video is publishing again. (BDEV-4780)
			dispatch(setRestrictedUserState(opentok.current.computeRestrictedState()));
		}

		opentok.current?.setLocalVideo(video);
		opentok.current?.setLocalAudio(audio);

		if (!isOnsitePublisher && !isOnsiteLimitedInput) {
			dispatch(addPresenterVideo({
				id: newPublisher?.streamId,
				srcObject: mainVideoStream,
				videoMuted: !video,
				muted: !audio,
				mutedPreference: !audio,
				userInitiated: !audio,
				self: true,
				local: true,
				profile: user?.gr_user ?? 1,
				brandliveProfileId: user?.brandlive_profile,
			}));
		}

		return 1;
	};

	useEffect(() => {
		if (!mainVideoStream && opentok.current?.publisher?.stream) {
			opentok.current.stopPublishing();
		}
	}, [mainVideoStream]);

	useEffect(() => {
		if (shouldRepublishVideo) {
			dispatch(setShouldRepublishVideo(false));

			if (
				!embed
				&& !watcherOnly
				&& !expectIosStream
				&& !!user
				&& opentokInitialized
			) {
				restartPublisher();
			}
		}
	}, [
		shouldRepublishVideo,
		opentokInitialized,
		embed,
		watcherOnly,
		expectIosStream,
		user,
	]);

	const prevSharingScreen = usePrevious(sharingScreen);
	const prevSocket = usePrevious(socket);

	useEffect(() => {
		if (prevSharingScreen !== sharingScreen) {
			if (sharingScreen) {
				startupOpentokScreenshare();
			} else {
				const destroyed = opentok.current?.stopScreensharing();

				if (destroyed) {
					let streams = connectedPresenterVideos;
					streams = streams.filter((stream: VideoStream) => stream.id !== String(destroyed));
					dispatch(setConnectedPresenterVideos(streams));
				}
			}
		}
	}, [sharingScreen, connectedPresenterVideos, prevSharingScreen]);

	const alertsHidden = useRef(true);
	useEffect(() => {
		// prevent alerts (specifically speakers) from showing on initial load
		const canShow = setTimeout(() => alertsHidden.current = false, 1000);
		return () => clearTimeout(canShow);
	}, []);

	const prevSelectedOutputRef = useRef<MediaDeviceInfo | null>(null);

	const deviceChangeSuccess = (deviceType: string, selectedDevice: MediaDeviceInfo, messageType: string) => {
		if (alertsHidden.current) return;
		showAlert({
			message: `${deviceType} Detected`,
			description: `Connected to ${selectedDevice.label}`,
			type: `${messageType}`,
		});
	};

	const deviceChangeError = (deviceType: string, selectedDevice: MediaDeviceInfo, messageType: string) => {
		showAlert({
			message: `Error Updating ${deviceType}`,
			description: `Cannot switch to ${deviceType} ${selectedDevice.label}`,
			type: `${messageType}`,
		});
	};

	const changeVideoDevice = useCallback((newSelectedVideo: MediaDeviceInfo) => {
		if (opentok.current && opentok.current?.publisher && newSelectedVideo?.deviceId) {
			opentok.current.changeVideoStream(newSelectedVideo.deviceId)
				.then((result) => {
					if (!result) {
						deviceChangeError('Video Device', newSelectedVideo, 'error');
					} else {
						deviceChangeSuccess('Video Device', newSelectedVideo, 'cam-update');
						dispatch(setSelectedVideo(newSelectedVideo));
					}
				}).catch((e) => {
					console.log(e);
				});
		} else {
			deviceChangeSuccess('Video Device', newSelectedVideo, 'cam-update');
			dispatch(setSelectedVideo(newSelectedVideo));
		}
	}, []);

	const changeAudioDevice = useCallback((newSelectedAudio: MediaDeviceInfo) => {
		if (opentok.current && opentok.current?.publisher && newSelectedAudio?.deviceId) {
			opentok.current.changeAudioStream(newSelectedAudio.deviceId)
				.then((result) => {
					if (!result) {
						deviceChangeError('Microphone Device', newSelectedAudio, 'error');
					} else {
						deviceChangeSuccess('Audio Device', newSelectedAudio, 'mic-update');
						dispatch(setSelectedAudio(newSelectedAudio));
					}
				}).catch((e) => {
					console.log(e);
				});
		} else {
			deviceChangeSuccess('Audio Device', newSelectedAudio, 'mic-update');
			dispatch(setSelectedAudio(newSelectedAudio));
		}
	}, []);

	const stopOT = () => {
		opentok.current?.session.disconnect();
		opentok.current?.stop();
		opentok.current = null;
	};

	useEffect(() => {
		opentokElements.current.forEach((el: HTMLVideoElement) => {
			if (
				el
				&& el.srcObject
				&& selectedOutput
				&& selectedOutput.deviceId
				&& (el.srcObject as MediaStream).getAudioTracks()?.[0]
			) {
				(el as any).setSinkId(selectedOutput.deviceId).catch((e: Error) => {
					deviceChangeError('Speaker', selectedOutput, 'error');
					console.error('could not set audio output', e.toString());
				});
			}
		});

		if (selectedOutput && selectedOutput.deviceId) {
			const prev = prevSelectedOutputRef.current;
			const identityChanged = prev === null
				|| prev.deviceId !== selectedOutput.deviceId
				|| (selectedOutput.deviceId === 'default' && prev.label !== selectedOutput.label);
			if (identityChanged) {
				deviceChangeSuccess('Speaker', selectedOutput, 'speaker-update');
			}
		}
		prevSelectedOutputRef.current = selectedOutput;
	}, [selectedOutput]);

	// giant componentDidUpdate from video-feed.tsx
	useEffect(() => {
		if (
			opentok.current?.session
			&& opentok.current.session.isConnected()
			&& restrictedUserState === RESTRICTED_USER_STATES.RECONNECTING
			&& (!embed || !watcherOnly)
			&& !opentok.current?.publisher
			&& mainVideoStream
		) {
			dispatch(setRestrictedUserState(RESTRICTED_USER_STATES.BLOCKED));
		}

		/*
			If component initialized with socket and connection time changed,
			Run initial setup again to get new instance of OpenTok class.
			Socket connection time only changes when initially connecting and
			then when reconnecting.
		*/
		if (socket?.connectionTimestamp
			&& prevSocket?.connectionTimestamp
			&& socket?.connectionTimestamp !== prevSocket?.connectionTimestamp
		) {
			initialSetup();
		}

		if ((unmutedAudioFeeds?.length ?? 0) > 0) {
			unmutedAudioFeeds.forEach((streamId) => {
				if (opentok.current) {
					opentok.current.resubscribeToStreamIfVideoMuted(streamId);
				}
				dispatch({
					type: OPENTOK_STREAM_AUDIO_REMOVE_UNMUTED_ID,
					payload: streamId,
				});
			});
		}
	});

	useEffect(() => {
		const volumeUpdates = new Map<string, number>();

		// Calculate all volume updates first
		const elements = opentokElements.current;
		const liveIdsNoMedia = canvasWebRTCIds.filter((id) => !id.includes('media-share'));

		// Set default volumes
		elements.forEach((el) => volumeUpdates.set(el.id, 1.0));

		// If this is an onsite program feed view we want to make the onsite users volume muted.
		// FYI - Onsite users can be turned on/off with right
		// click menu in thumbnail feeds by a producer so we need to listen to onsiteUsers
		// changes and adjust that user's volume on and off.
		// If an onsite user is dragged from feeds -> canvas need to adjust volume in shot
		if (onsiteUsers && onsiteUsers.length > 0) {
			connectedPresenterVideos.forEach((vid) => {
				if (vid.profile && onsiteUsers.includes(vid.profile)) {
					volumeUpdates.set(vid.id, 0.0);
				}
			});
		}

		// backstage comms
		/*
			this is an automatic stream mute that happens based off of who is live (in canvas)
			if a stream is live, we don't want them to hear anyone who isn't live
			so if a stream goes live, we tell that stream's matching application that every
			stream that isn't live should be muted, and every stream that is live should have audio out
			THIS IS DIFFERENT THAN A REGULAR MUTE
			a regular mute sets the person's audio output to 0 volume, and is reflected in their UI
			this mutes the actual video element, is automatic, and has no UI indication
		*/
		if (!embed && programView === ProgramViewType.None && webrtcID) {
			const isInShot = liveIdsNoMedia.includes(webrtcID.toString());
			const shouldMuteNonLive = isInShot && backstageCommsActive
				&& roomState === ROOM_STATES.SHOWTIME && !clearShot;

			if (shouldMuteNonLive) {
				elements.forEach((el) => {
					volumeUpdates.set(el.id, liveIdsNoMedia.includes(el.id) ? 1.0 : 0.0);
				});
			}
		}

		/*
			embed view backstage chat - we only want the embed
			view to hear streams that are in the canvas
		*/
		if (embed) {
			elements.forEach((el) => {
				let shouldBeMuted = !liveIdsNoMedia.includes(el.id);

				if (!backstageCommsActive) shouldBeMuted = false;
				if (clearShot) shouldBeMuted = true;

				// mute all opentok feeds in showbuilder for embed views
				if (roomState === ROOM_STATES.EDITING) shouldBeMuted = true;

				// Check onsite program feed
				if (programView === ProgramViewType.OnsiteFeed && onsiteUsers) {
					const userId = connectedPresenterVideos.find((vid) => vid.id === el.id)?.profile;
					if (userId && onsiteUsers.includes(userId)) {
						shouldBeMuted = true;
					}
				}

				// Check onsite location canvas
				if (
					onsiteLocationSession
					&& LOCATION_CANVAS_ROUTES.includes(onsiteLocationSession.route)
					&& onsiteLocationSession.id
				) {
					const locationStreams = onsiteLocationStreams.get(onsiteLocationSession.id);
					if (locationStreams?.some((s) => s.id === el.id)) {
						shouldBeMuted = true;
					}
				}

				volumeUpdates.set(el.id, shouldBeMuted ? 0.0 : 1.0);
			});
		}

		// Apply canvas volume override
		if (backstageCanvasVolumeOn) {
			elements.forEach((el) => {
				if (canvasWebRTCIds.includes(el.id)) {
					volumeUpdates.set(el.id, backstageCanvasVolume);
				}
			});
		}

		// Apply all volume updates at once
		elements.forEach((el) => {
			const volume = volumeUpdates.get(el.id) ?? 1.0;
			el.volume = volume;
			if (opentok.current && opentok.current.subscribers[el.id]) {
				opentok.current.subscribers[el.id].setAudioVolume(volume * 100);
			}
		});
	}, [
		shotFeeds,
		connectedPresenterVideos,
		backstageCommsActive,
		roomState,
		clearShot,
		onsiteUsers,
		programView,
		canvasWebRTCIds,
		embed,
		backstageCanvasVolume,
		backstageCanvasVolumeOn,
		onsiteLocationSession,
		onsiteLocationStreams,
	]);

	useEffect(() => {
		if (embed || programView !== ProgramViewType.None || !webrtcID) return;
		socket?.removeListener('backstage-comms-toggle');
		socket?.addListener(
			'backstage-comms-toggle',
			(message: any) => {
				applyBackstageComms(
					message,
					canvasWebRTCIds,
					clearShot,
					webrtcID.toString(),
				);
			},
		);
	}, [clearShot, webrtcID, canvasWebRTCIds, embed, socket, programView]);

	useEffect(() => {
		if (embed || programView !== ProgramViewType.None || !webrtcID) return;
		if (canvasWebRTCIds.includes(webrtcID.toString())) {
			openCommValues?.forEach((value) => {
				const producer = connectedUsers.find((connUser) => connUser.gr_user === value.producer);
				if (!producer || !producer.webrtcID) return;
				applyBackstageComms(
					{
						type: 'backstage-comms-toggle',
						message: {
							muted: false,
							producerID: producer.webrtcID.toString(),
							targetedPresenterID: value.presenter ?? '',
						},
					} as BackstageCommsSocketMessage,
					canvasWebRTCIds,
					clearShot,
					webrtcID.toString(),
				);
			});
		}
	}, [clearShot, canvasWebRTCIds, webrtcID, openCommValues, embed, programView]);

	// Sweeps every stream already in the session. isoArchiveId only arrives once the archive
	// reports STARTED, long after presenters join, so the streams that joined before the
	// recording started are registered here. Also retries streams the join path skipped for
	// missing identity, once their user or location lands.
	useEffect(() => {
		// connectedUsers churns constantly; skip the sweep entirely outside a recording.
		if (!isoArchiveId) return;

		opentok.current?.streams?.forEach((stream) => registerArchiveStream(stream, {
			isoArchiveId,
			embed,
			token,
			meetingId: meeting?.meeting,
			connectedUsers,
			onsiteLocations,
		}));
	}, [
		isoArchiveId,
		opentok.current?.session?.sessionId,
		meeting?.meeting,
		embed,
		token,
		connectedUsers,
		onsiteLocations,
	]);

	useEffect(() => {
		if (
			!startedUp.current
			|| !user
			|| typeof prevUseAdvancedAudio === 'undefined'
			|| typeof prevUseEnable1080p === 'undefined'
		) return;

		if (useAdvancedAudio !== prevUseAdvancedAudio || useEnable1080p !== prevUseEnable1080p) {
			dispatch(setShouldRepublishVideo(true));
		}
	}, [useAdvancedAudio, prevUseAdvancedAudio, useEnable1080p, prevUseEnable1080p]);

	const prevShowDebugUI = usePrevious(showDebugUI);
	useEffect(() => {
		if (showDebugUI) {
			// clear the countdown when reopening the debug ui
			if (logOTStatsCountdown.current) clearTimeout(logOTStatsCountdown.current);
			setShouldLogOTStats(true);
		} else if (!showDebugUI && prevShowDebugUI) {
			// if turning off the video troubleshooting ui, continue logging for an hour
			logOTStatsCountdown.current = setTimeout(() => setShouldLogOTStats(false), 1000 * 60 * 60);
		}

		return () => clearTimeout(logOTStatsCountdown.current);
	}, [showDebugUI]);

	useEffect(() => {
		if (shouldLogOTStats) {
			// log stats every 5 seconds
			logOTStatsInterval.current = setInterval(() => opentok.current?.logOpentokStats(), 1000 * 5);
		} else {
			clearInterval(logOTStatsInterval.current);
		}

		return () => clearInterval(logOTStatsInterval.current);
	}, [shouldLogOTStats, opentok.current]);

	useEffect(() => {
		// Don't monitor audio while an input is permission-restricted: the kept-alive
		// publisher is intentionally muted, so stalled bytesSent is expected, not a fault.
		const restrictedMicStates = [
			RESTRICTED_USER_STATES.CAMERA,
			RESTRICTED_USER_STATES.MICROPHONE,
			RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE,
		];

		// the following is a snippet from Vonage to detect if a publisher is no longer sending audio.
		// it will be implemented in a future OpenTok Client SDK release,
		// likely as a method called monitorAudioStream which should replace this snippet.
		if (opentok.current?.publisher && !restrictedMicStates.includes(restrictedUserState)) {
			let lastBytesSent = 0;
			const MONITOR_FREQUENCY_SECONDS = 2;
			opentokAudioMonitor.current = setInterval(() => {
				opentok.current?.publisher?.getStats((error, publisherStats) => {
					if (!publisherStats) {
						return;
					}

					let totalBytesSent = 0;

					publisherStats.forEach(({ stats }) => {
						if (stats?.audio?.bytesSent) {
							totalBytesSent += stats.audio?.bytesSent;
						}
					});

					totalBytesSent %= Number.MAX_SAFE_INTEGER;

					// Guard against false positives on startup when the publisher hasn't sent audio yet.
					// Read restrictedUserState fresh — the closure can be stale before a re-render.
					const { SessionReducer: freshSession } = store.getState() as GRS;
					const currentRestrictedState = freshSession.restrictedUserState;
					if (
						totalBytesSent === lastBytesSent
						&& lastBytesSent > 0
						&& !restrictedMicStates.includes(currentRestrictedState)
					) {
						logger.error('publisher is no longer sending audio. restarting.');
						dispatch(setShouldRepublishVideo(true));
					}

					lastBytesSent = totalBytesSent;
				});
			}, MONITOR_FREQUENCY_SECONDS * 1000);
		}

		return () => clearInterval(opentokAudioMonitor.current);
	}, [opentok.current?.publisher, restrictedUserState]);

	useEffect(() => {
		if (
			selectedVideo
			&& selectedAudio
			&& mainVideoStream
			&& !opentok.current?.session.isConnected()
		) {
			const videoTrack = mainVideoStream.getVideoTracks()[0];
			const audioTrack = mainVideoStream.getAudioTracks()[0];
			if (videoTrack?.readyState === 'ended' || audioTrack?.readyState === 'ended') {
				getCameraStream(selectedVideo.deviceId, selectedAudio.deviceId)
					.then((str) => dispatch(setMainVideoStream(str)));
			}
		}
	}, [selectedVideo, selectedAudio, mainVideoStream]);

	// if the meeting's opentok session id changes, reconnect
	useEffect(() => {
		if (opentok.current?.session?.sessionId
			&& (opentok.current.session.sessionId !== opentokSession)
		) {
			showAlert({
				message: 'Optimizing connection...',
				description: 'Reconnecting to an optimized media server',
				duration: 4000,
				type: 'info',
			});
			handleReconnect();
		}
	}, [opentok.current?.session, opentokSession]);

	return {
		opentok: opentok.current,
		forceFeedsTabRefresh,
		setOT,
		initialSetup,
		restartPublisher,
		changeAudioDevice,
		changeVideoDevice,
		stopOT,
		startupOpentokScreenshare,
	};
}

export const OpenTokProvider = (
	props: any,
): React.ReactElement<any, React.Provider<OpenTokContextInterface>> => {
	const opentokContextValue = useOpentok(props.socket);
	return <OpenTokContext.Provider value={opentokContextValue}>
		{props.children}
	</OpenTokContext.Provider>;
};
